import { DateFormatter } from "../../../functions/DateFormatter";
import { obterDadosUsuario2, guardarDadosUsuario, guardarDadosUsuario2 } from "../../../services/cookies";
import { obterHistoricoFechamento } from "../../../services/fechamento";
import { obterIdFundoAcessoria } from "../../../services/fundoFinanceiro";
import { obterRankingDePosicoes } from "../../../services/gestor";
import { obterFundoFinanceiro, renovacaoToken } from "../../../services/usuario";
import { Fechamentos } from "../HistoricoFechamento/HistoricoFechamento";

type StateSetter<T> = (value: React.SetStateAction<T>) => void;

export interface RankingClientes {
  idCredenciais: string;
  nome: string;
  totalCotas: number;
}

export const gerarNumerosAleatorios = (min: number, max: number, times: number) => {
  const arrayAleatorios: number[] = [];

  for (let i = 0; i < times; i += 1) {
    arrayAleatorios.push(Math.floor(Math.random() * (max - min) + min));
  }

  return arrayAleatorios;
};

export const gerarCoresAleatorias = (quantidade: number, colorArray: string[]) => {
  const palette = [
    "#0e1828",
    "#ff9f2f",
    "#00b6a4",
    "#2f80ed",
    "#5f718f",
    "#008c7e",
    "#ffbd6b",
    "#78b6ff",
    "#203149",
    "#c76a00",
  ];

  for (let i = 0; i < quantidade; i += 1) {
    colorArray.push(...palette);
  }

  return colorArray;
};

const erroNaoAutorizado = (err: any) =>
  err?.response?.status === 401 || String(err) === "Error: Request failed with status code 401";

const guardarTokensRenovados = (res: any) => {
  const dadosUsuario = obterDadosUsuario2();
  const tokenAcesso = res.data.camposAdicionais.tokenAcesso;
  const tokenRenovacao = res.data.camposAdicionais.tokenRenovacao;

  guardarDadosUsuario(
    tokenAcesso,
    tokenRenovacao,
    dadosUsuario.email.toString(),
    dadosUsuario.id.toString()
  );
  guardarDadosUsuario2(
    tokenAcesso,
    tokenRenovacao,
    dadosUsuario.email.toString(),
    dadosUsuario.id.toString(),
    dadosUsuario.urlAvatar
  );

  return { tokenAcesso, tokenRenovacao };
};

export async function handleGraficosDashboards(
  setLoadingGrafico: StateSetter<boolean>,
  setErrorGrafico: StateSetter<boolean>,
  dataInicial: Date,
  dataFinal: Date,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>,
  setRendimentosDia: StateSetter<number[]>,
  setValoresTotais: StateSetter<number[]>,
  setLabels: StateSetter<string[]>,
  setValoresAcumulados: StateSetter<number[]>,
  idFundoFinaceiro: string,
  token: string,
  tokenRenovacao: string
) {
  setLoadingGrafico(true);
  setErrorGrafico(false);

  const dataInicialZerada = new Date(new Date(dataInicial).setHours(0)).toISOString();

  try {
    const res = await obterHistoricoFechamento(
      dataInicialZerada,
      dataFinal.toISOString(),
      false,
      token,
      idFundoFinaceiro
    );
    pegaDadosGrafico(
      res.data.resultado.fechamentos,
      setRendimentosDia,
      setValoresTotais,
      setLabels,
      setValoresAcumulados
    );
  } catch (err) {
    if (!erroNaoAutorizado(err)) {
      setErrorGrafico(true);
      return;
    }

    try {
      const resToken = await renovacaoToken(token, tokenRenovacao);
      const tokens = guardarTokensRenovados(resToken);
      const resHistorico = await obterHistoricoFechamento(
        dataInicialZerada,
        dataFinal.toISOString(),
        false,
        tokens.tokenAcesso,
        idFundoFinaceiro
      );

      pegaDadosGrafico(
        resHistorico.data.resultado.fechamentos,
        setRendimentosDia,
        setValoresTotais,
        setLabels,
        setValoresAcumulados
      );
    } catch {
      setModalDesconectar(true);
      setErrorGrafico(true);
    }
  } finally {
    setLoadingGrafico(false);
  }
}

export const pegaDadosGrafico = (
  arrayFechamentos: Fechamentos[],
  setRendimentosDia: StateSetter<number[]>,
  setValoresTotais: StateSetter<number[]>,
  setLabels: StateSetter<string[]>,
  setValoresAcumulados: StateSetter<number[]>
) => {
  const rendimentoParciais: number[] = [];
  const valoresParciais: number[] = [];
  const valoresAcumuladosParciais: number[] = [];
  const labelsParciais: string[] = [];

  arrayFechamentos.forEach((fechamentoDiario, index, array) => {
    rendimentoParciais.push(fechamentoDiario.valorTotalDolar);
    labelsParciais.push(DateFormatter(fechamentoDiario.data));

    valoresParciais.push(
      index === 0
        ? 0
        : ((fechamentoDiario.valorCota / array[index - 1].valorCota) - 1) * 100
    );
    valoresAcumuladosParciais.push((fechamentoDiario.valorCota / 1101.1) * 100 - 100);
  });

  setRendimentosDia(rendimentoParciais);
  setValoresTotais(valoresParciais);
  setValoresAcumulados(valoresAcumuladosParciais);
  setLabels(labelsParciais);
};

export async function handleGraficosRankingClientes(
  setLoadingGrafico: StateSetter<boolean>,
  setErrorGrafico: StateSetter<boolean>,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>,
  setPorcentagensRanking: React.Dispatch<React.SetStateAction<number[]>>,
  setLabelsRanking: React.Dispatch<React.SetStateAction<string[]>>
) {
  const { token, tokenRenovacao } = obterDadosUsuario2();

  setLoadingGrafico(true);
  setErrorGrafico(false);

  try {
    const res = await obterRankingDePosicoes(1, 1000, token);
    mapearDadosRankingParaGrafico(
      res.data.resultado.clientes,
      setPorcentagensRanking,
      setLabelsRanking
    );
  } catch (err) {
    if (!erroNaoAutorizado(err)) {
      setErrorGrafico(true);
      return;
    }

    try {
      const resToken = await renovacaoToken(token, tokenRenovacao);
      const tokens = guardarTokensRenovados(resToken);
      const resRanking = await obterRankingDePosicoes(1, 1000, tokens.tokenAcesso);

      mapearDadosRankingParaGrafico(
        resRanking.data.resultado.clientes,
        setPorcentagensRanking,
        setLabelsRanking
      );
    } catch {
      setModalDesconectar(true);
      setErrorGrafico(true);
    }
  } finally {
    setLoadingGrafico(false);
  }
}

export const mapearDadosRankingParaGrafico = (
  arrayRankingClientes: RankingClientes[],
  setPorcentagensRanking: React.Dispatch<React.SetStateAction<number[]>>,
  setLabelsRanking: React.Dispatch<React.SetStateAction<string[]>>
) => {
  setPorcentagensRanking(arrayRankingClientes.map((item) => item.totalCotas));
  setLabelsRanking(arrayRankingClientes.map((item) => item.nome));
};

export const obterIdFundoFinanceiroAssessoria = async (
  setLoadingGrafico: StateSetter<boolean>,
  setErrorGrafico: StateSetter<boolean>,
  dataInicial: Date,
  dataFinal: Date,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>,
  setRendimentosDia: StateSetter<number[]>,
  setValoresTotais: StateSetter<number[]>,
  setLabels: StateSetter<string[]>,
  setValoresAcumulados: StateSetter<number[]>
) => {
  const { email, id, token, tokenRenovacao, urlAvatar } = obterDadosUsuario2();
  setLoadingGrafico(true);
  setErrorGrafico(false);

  try {
    const res = await obterIdFundoAcessoria(token);
    await handleGraficosDashboards(
      setLoadingGrafico,
      setErrorGrafico,
      dataInicial,
      dataFinal,
      setModalDesconectar,
      setRendimentosDia,
      setValoresTotais,
      setLabels,
      setValoresAcumulados,
      res.data.resultado.fundoFinanceiro.id,
      token,
      tokenRenovacao
    );
  } catch (err) {
    if (!erroNaoAutorizado(err)) {
      setErrorGrafico(true);
      setLoadingGrafico(false);
      return;
    }

    try {
      const resToken = await renovacaoToken(token, tokenRenovacao);
      guardarDadosUsuario2(
        resToken.data.camposAdicionais.tokenAcesso,
        resToken.data.camposAdicionais.tokenRenovacao,
        email.toString(),
        id.toString(),
        urlAvatar
      );
      const resFundo = await obterIdFundoAcessoria(resToken.data.camposAdicionais.tokenAcesso);

      await handleGraficosDashboards(
        setLoadingGrafico,
        setErrorGrafico,
        dataInicial,
        dataFinal,
        setModalDesconectar,
        setRendimentosDia,
        setValoresTotais,
        setLabels,
        setValoresAcumulados,
        resFundo.data.resultado.fundoFinanceiro.id,
        resToken.data.camposAdicionais.tokenAcesso,
        resToken.data.camposAdicionais.tokenRenovacao
      );
    } catch {
      setModalDesconectar(true);
      setErrorGrafico(true);
      setLoadingGrafico(false);
    }
  }
};

export const obterIdFundoFinanceiroPrivate = async (
  setLoadingGrafico: StateSetter<boolean>,
  setErrorGrafico: StateSetter<boolean>,
  dataInicial: Date,
  dataFinal: Date,
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>,
  setRendimentosDia: StateSetter<number[]>,
  setValoresTotais: StateSetter<number[]>,
  setLabels: StateSetter<string[]>,
  setValoresAcumulados: StateSetter<number[]>,
  idCredenciais: string
) => {
  const { email, id, token, tokenRenovacao, urlAvatar } = obterDadosUsuario2();
  setLoadingGrafico(true);
  setErrorGrafico(false);

  try {
    const res = await obterFundoFinanceiro(token, idCredenciais);
    const fundoPrivate = res.data.resultado.clientes.find(
      (fundo: { id: string; servicoFinanceiro: string }) => fundo.servicoFinanceiro === "Private"
    );

    await handleGraficosDashboards(
      setLoadingGrafico,
      setErrorGrafico,
      dataInicial,
      dataFinal,
      setModalDesconectar,
      setRendimentosDia,
      setValoresTotais,
      setLabels,
      setValoresAcumulados,
      fundoPrivate.id,
      token,
      tokenRenovacao
    );
  } catch (err) {
    if (!erroNaoAutorizado(err)) {
      setErrorGrafico(true);
      setLoadingGrafico(false);
      return;
    }

    try {
      const resToken = await renovacaoToken(token, tokenRenovacao);
      guardarDadosUsuario2(
        resToken.data.camposAdicionais.tokenAcesso,
        resToken.data.camposAdicionais.tokenRenovacao,
        email.toString(),
        id.toString(),
        urlAvatar
      );
      const resFundo = await obterFundoFinanceiro(
        resToken.data.camposAdicionais.tokenAcesso,
        idCredenciais
      );
      const fundoPrivate = resFundo.data.resultado.clientes.find(
        (fundo: { id: string; servicoFinanceiro: string }) =>
          fundo.servicoFinanceiro === "Private"
      );

      await handleGraficosDashboards(
        setLoadingGrafico,
        setErrorGrafico,
        dataInicial,
        dataFinal,
        setModalDesconectar,
        setRendimentosDia,
        setValoresTotais,
        setLabels,
        setValoresAcumulados,
        fundoPrivate.id,
        resToken.data.camposAdicionais.tokenAcesso,
        resToken.data.camposAdicionais.tokenRenovacao
      );
    } catch {
      setModalDesconectar(true);
      setErrorGrafico(true);
      setLoadingGrafico(false);
    }
  }
};
